import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { Modal } from '../components/Modal';

type Unit = 'night' | 'week' | 'hour';

interface PricingRuleRow {
  id: number;
  rule_name: string;
  vessel_length_min: number;
  vessel_length_max: number;
  price_per_unit: number;
  unit: string;
  electricity_surcharge: number;
  currency: string;
  valid_from: string | null;
  valid_to: string | null;
  season: string | null;
  priority: number;
}

interface RuleFormState {
  rule_name: string;
  vessel_length_min: string;
  vessel_length_max: string;
  price_per_unit: string;
  unit: Unit;
  electricity_surcharge: string;
  currency: string;
  valid_from: string;
  valid_to: string;
  season: string;
  priority: string;
}

const EMPTY_FORM: RuleFormState = {
  rule_name: 'daily',
  vessel_length_min: '0',
  vessel_length_max: '12',
  price_per_unit: '',
  unit: 'night',
  electricity_surcharge: '0',
  currency: 'EUR',
  valid_from: '',
  valid_to: '',
  season: '',
  priority: '10',
};

/** Canonical slugs used by backend `calculatePrice` — typos break weekly remainders and €/m tiers. */
const PRICING_RULE_NAME_IDS = ['daily', 'daily_e', 'weekly', 'weekly_e', 'short_stay'] as const;
type PricingRuleNameId = (typeof PRICING_RULE_NAME_IDS)[number];

function isKnownPricingRuleName(s: string): s is PricingRuleNameId {
  return (PRICING_RULE_NAME_IDS as readonly string[]).includes(s);
}

function pricingRuleDisplayName(t: (key: string) => string, ruleName: string): string {
  const id = ruleName.trim();
  return isKnownPricingRuleName(id) ? t(`pricing.ruleNames.${id}`) : id;
}

/** Matches backend `nightPricePerNight` / `weekPricePerWeek` per-metre semantics. */
function formatBerthPriceCell(
  t: (key: string, opts?: Record<string, string>) => string,
  r: Pick<PricingRuleRow, 'rule_name' | 'unit' | 'price_per_unit' | 'currency'>,
): string {
  const amount = Number(r.price_per_unit).toFixed(2);
  const currency = r.currency ?? 'EUR';
  const name = r.rule_name.trim();
  const unit = r.unit;
  if (name === 'daily_e' && unit === 'night') {
    return t('pricing.priceCellPerMeterNight', { amount, currency });
  }
  if (name === 'weekly_e' && unit === 'week') {
    return t('pricing.priceCellPerMeterWeek', { amount, currency });
  }
  if (unit === 'hour') return t('pricing.priceCellHour', { amount, currency });
  if (unit === 'week') return t('pricing.priceCellWeek', { amount, currency });
  return t('pricing.priceCellNight', { amount, currency });
}

function rowToForm(r: PricingRuleRow): RuleFormState {
  const dateIn = (v: string | null) => (v ? v.slice(0, 10) : '');
  return {
    rule_name: r.rule_name.trim(),
    vessel_length_min: String(r.vessel_length_min),
    vessel_length_max: String(r.vessel_length_max),
    price_per_unit: String(r.price_per_unit),
    unit: (['night', 'week', 'hour'].includes(r.unit) ? r.unit : 'night') as Unit,
    electricity_surcharge: String(r.electricity_surcharge ?? 0),
    currency: r.currency ?? 'EUR',
    valid_from: dateIn(r.valid_from),
    valid_to: dateIn(r.valid_to),
    season: r.season ?? '',
    priority: String(r.priority ?? 0),
  };
}

function parseNum(s: string): number | null {
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function PricingPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeMarinaId } = useAuth();
  const [rules, setRules] = useState<PricingRuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editRule, setEditRule] = useState<PricingRuleRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PricingRuleRow | null>(null);
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getPricing();
      const list = (data.rules ?? []) as unknown as PricingRuleRow[];
      list.sort((a, b) => {
        if (b.priority !== a.priority) return b.priority - a.priority;
        return a.vessel_length_min - b.vessel_length_min;
      });
      setRules(list);
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeMarinaId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditRule(null);
    setShowAdd(true);
  };

  const openEdit = (r: PricingRuleRow) => {
    setForm(rowToForm(r));
    setEditRule(r);
    setShowAdd(true);
  };

  const closeModal = () => {
    if (!saving) {
      setShowAdd(false);
      setEditRule(null);
    }
  };

  const buildPayload = (): Record<string, unknown> | null => {
    const rule_name = form.rule_name.trim();
    const minL = parseNum(form.vessel_length_min);
    const maxL = parseNum(form.vessel_length_max);
    const price = parseNum(form.price_per_unit);
    const elec = parseNum(form.electricity_surcharge);
    const pri = parseInt(form.priority, 10);

    if (!rule_name) {
      toast.error(t('pricing.ruleNameRequired'));
      return null;
    }
    if (minL === null || maxL === null || minL >= maxL) {
      toast.error(t('pricing.lengthInvalid'));
      return null;
    }
    if (price === null || price < 0) {
      toast.error(t('pricing.priceInvalid'));
      return null;
    }
    if (elec === null || elec < 0) {
      toast.error(t('pricing.elecInvalid'));
      return null;
    }
    if (!Number.isFinite(pri)) {
      toast.error(t('pricing.priorityInvalid'));
      return null;
    }

    const validFrom = form.valid_from.trim() || null;
    const validTo = form.valid_to.trim() || null;
    if (validFrom && validTo && validFrom > validTo) {
      toast.error(t('pricing.dateRangeInvalid'));
      return null;
    }

    const payload: Record<string, unknown> = {
      rule_name,
      vessel_length_min: minL,
      vessel_length_max: maxL,
      price_per_unit: price,
      unit: form.unit,
      electricity_surcharge: elec,
      currency: form.currency.trim() || 'EUR',
      valid_from: validFrom,
      valid_to: validTo,
      season: form.season.trim() || null,
      priority: pri,
    };
    return payload;
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    try {
      if (editRule) {
        await api.updatePricingRule(editRule.id, payload);
        toast.success(t('pricing.ruleUpdated'));
      } else {
        await api.createPricingRule(payload);
        toast.success(t('pricing.ruleCreated'));
      }
      setShowAdd(false);
      setEditRule(null);
      fetchRules();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await api.deletePricingRule(deleteTarget.id);
      toast.success(t('pricing.ruleDeleted'));
      setDeleteTarget(null);
      fetchRules();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.failedToDelete'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  const modalTitle = editRule ? t('pricing.editRule') : t('pricing.addRule');

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('pricing.title')}</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">{t('pricing.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" /> {t('pricing.addRule')}
        </button>
      </div>

      <div className="flex gap-2 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2.5 text-sm text-amber-900">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <p>{t('pricing.kbHint')}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {rules.length === 0 ? (
          <div className="px-5 py-16 text-center text-sm text-gray-400">{t('pricing.noRules')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">
                    {t('pricing.colName')}
                  </th>
                  <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">
                    {t('pricing.colLength')}
                  </th>
                  <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">
                    {t('pricing.colPrice')}
                  </th>
                  <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden lg:table-cell whitespace-nowrap">
                    {t('pricing.colElec')}
                  </th>
                  <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell whitespace-nowrap">
                    {t('pricing.colSeason')}
                  </th>
                  <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden sm:table-cell whitespace-nowrap">
                    {t('pricing.colPriority')}
                  </th>
                  <th className="text-right px-3 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rules.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-3 py-3 font-medium text-gray-900" title={r.rule_name}>{pricingRuleDisplayName(t, r.rule_name)}</td>
                    <td className="px-3 py-3 text-gray-700 whitespace-nowrap">
                      {r.vessel_length_min}–{r.vessel_length_max} m
                    </td>
                    <td className="px-3 py-3 text-gray-700 whitespace-nowrap">
                      {formatBerthPriceCell(t, r)}
                    </td>
                    <td className="px-3 py-3 text-gray-700 hidden lg:table-cell">
                      {Number(r.electricity_surcharge).toFixed(2)} {r.currency}
                    </td>
                    <td className="px-3 py-3 text-gray-600 hidden md:table-cell">{r.season ?? '—'}</td>
                    <td className="px-3 py-3 text-gray-600 hidden sm:table-cell">{r.priority}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(r)}
                          className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(r)}
                          className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={showAdd} onClose={closeModal} title={modalTitle} maxWidth="max-w-lg">
        <div className="px-5 py-4 space-y-3 overflow-y-auto max-h-[65vh]">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.ruleName')}</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
              value={form.rule_name}
              onChange={(e) => setForm((f) => ({ ...f, rule_name: e.target.value }))}
            >
              {!isKnownPricingRuleName(form.rule_name.trim()) && form.rule_name.trim() !== '' ? (
                <option value={form.rule_name}>{t('pricing.ruleNameLegacyOption', { name: form.rule_name })}</option>
              ) : null}
              {PRICING_RULE_NAME_IDS.map((id) => (
                <option key={id} value={id}>{t(`pricing.ruleNames.${id}`)}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1.5">{t('pricing.ruleNameHint')}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.vesselLengthMin')}</label>
              <input
                type="text"
                inputMode="decimal"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.vessel_length_min}
                onChange={(e) => setForm((f) => ({ ...f, vessel_length_min: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.vesselLengthMax')}</label>
              <input
                type="text"
                inputMode="decimal"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.vessel_length_max}
                onChange={(e) => setForm((f) => ({ ...f, vessel_length_max: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.pricePerUnit')}</label>
              <input
                type="text"
                inputMode="decimal"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.price_per_unit}
                onChange={(e) => setForm((f) => ({ ...f, price_per_unit: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.unit')}</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
                value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value as Unit }))}
              >
                <option value="night">{t('pricing.unitNight')}</option>
                <option value="week">{t('pricing.unitWeek')}</option>
                <option value="hour">{t('pricing.unitHour')}</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.electricitySurcharge')}</label>
              <input
                type="text"
                inputMode="decimal"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.electricity_surcharge}
                onChange={(e) => setForm((f) => ({ ...f, electricity_surcharge: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.currency')}</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm uppercase"
                maxLength={3}
                value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.validFrom')}</label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.valid_from}
                onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.validTo')}</label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.valid_to}
                onChange={(e) => setForm((f) => ({ ...f, valid_to: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.season')}</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.season}
                onChange={(e) => setForm((f) => ({ ...f, season: e.target.value }))}
                placeholder={t('pricing.seasonPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('pricing.priority')}</label>
              <input
                type="text"
                inputMode="numeric"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              />
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={closeModal}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </Modal>

      <Modal
        open={!!deleteTarget}
        onClose={() => !saving && setDeleteTarget(null)}
        title={t('pricing.deleteRule')}
        maxWidth="max-w-md"
      >
        <div className="px-5 py-4">
          <p className="text-sm text-gray-600">{t('pricing.deleteConfirm', { name: deleteTarget?.rule_name ?? '' })}</p>
          <p className="text-xs text-gray-400 mt-2">{t('common.thisActionCannotBeUndone')}</p>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {saving ? t('common.deleting') : t('common.delete')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
